import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Grid from "@material-ui/core/Grid";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import Currency from "./Currency";
import ImageLoader from "./ImageLoader";
import Product from "./Product";
import { generateItemGroup } from "./ProductDetails";

const useStyles = makeStyles(theme => ({
  badge: {
    zIndex: 2
  },
  crossedPrice: {
    textDecoration: "line-through",
    marginLeft: theme.spacing(1)
  },
  sale: {
    padding: theme.spacing(1),
    display: "inline-block",
    position: "absolute",
    top: "0",
    right: "0",
    fontSize: "1rem",
    color: theme.palette.common.white,
    background: theme.palette.secondary.main,
    borderTopRightRadius: "5px",
    zIndex: 2
  },
  featured: {
    height: "25px",
    display: "inline-block",
    position: "absolute",
    bottom: "10px",
    right: "calc(50% - 36px)",
    zIndex: 2,

    "&:after": {
      content: '"Destacado"',
      padding: theme.spacing(0.5),
      fontSize: "0.8rem",
      color: theme.palette.primary.main,
      background: "white",
      border: "1px solid",
      borderColor: theme.palette.primary.main,
      borderRadius: "3px"
    }
  },
  noStock: {
    height: "25px",

    "&:after": {
      content: '"Agotado"',
      padding: "5px",
      fontSize: "0.8rem",
      color: theme.palette.primary.main,
      border: "1px solid",
      borderColor: theme.palette.primary.main,
      borderRadius: "3px"
    }
  }
}));

const ProductListItem = ({ index, product, quantity, onSelected, onChange }) => {
  const classes = useStyles();
  const [stock, setStock] = useState(null);

  useEffect(() => {
    if (product && ((product.body || {}).features || []).length === 0) {
      setStock(Product.getStock(product, []));
    } else {
      setStock(null);
    }
  }, [product]);

  const updateItem = quantity => {
    const selectedFeatures = [];
    const price =
      product.body.sale && product.body.sale.percentage ? product.body.sale.value : product.price;

    const group = generateItemGroup(product.id, selectedFeatures);

    const item = {
      productID: product.id,
      price,
      quantity,
      total: price * quantity,
      body: { features: [] },
      group,
      product
    };

    if (group) {
      item.group = group;
    }

    const action = index === null ? "create" : quantity > 0 ? "update" : "delete";

    onChange(action, item, index);
  };

  const updateQuantity = quantity => {
    if (stock) {
      if (quantity <= stock.quantity) {
        updateItem(quantity);
      }
    } else {
      updateItem(quantity);
    }
  };

  const addItem = () => {
    const q = quantity + 1;
    updateQuantity(q);
  };

  const removeItem = () => {
    const q = quantity - 1;
    updateQuantity(q);
  };

  const renderButton = () => {
    if (stock && stock.quantity === 0) {
      return <div className={classes.noStock}></div>;
    }

    if (quantity === 0) {
      return (
        <Button variant="contained" className="action-button" onClick={addItem}>
          Agregar
        </Button>
      );
    } else {
      return (
        <ButtonGroup>
          <Button aria-label="reduce" size="small" disabled={quantity < 1} onClick={removeItem}>
            <RemoveIcon fontSize="small" />
          </Button>
          <Button aria-label="increase" size="small" onClick={addItem}>
            {stock && quantity >= stock.quantity ? "Máximo" : <AddIcon fontSize="small" />}
          </Button>
        </ButtonGroup>
      );
    }
  };

  return (
    <Badge
      badgeContent={quantity}
      color="secondary"
      anchorOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      classes={{ badge: classes.badge }}
    >
      <Card className="product-card mb-4">
        {product.body && product.body.features && product.body.features.length > 0 ? (
          <CardActionArea className="product-card-body" onClick={() => onSelected(product)}>
            <CardContent className="details">
              <div className="text-details">
                <Typography variant="body1">{product.name}</Typography>

                <Typography className="mt-1 description" variant="caption" color="textSecondary">
                  {product.body.description}
                </Typography>
              </div>

              <div className="d-flex align-items-center">
                {product.body.sale && product.body.sale.percentage ? (
                  <>
                    <Typography variant="subtitle1" color="primary">
                      <Currency value={product.body.sale.value} />
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      className={classes.crossedPrice}
                    >
                      <Currency value={product.price} />
                    </Typography>
                  </>
                ) : (
                  <Typography variant="subtitle2" color="textSecondary">
                    <Currency value={product.price} />
                  </Typography>
                )}
              </div>
            </CardContent>
            <CardMedia className="media" title={product.name}>
              {product.body.sale && product.body.sale.percentage && (
                <div className={classes.sale}>{product.body.sale.percentage}%</div>
              )}
              {product.body.featured && <div className={classes.featured}></div>}
              <ImageLoader
                imgKey={product.medias[0] && product.medias[0].key}
                circularProgressSize={30}
              />
            </CardMedia>
          </CardActionArea>
        ) : (
          <div className="product-card-body">
            <CardContent className="details">
              <div className="text-details" onClick={() => onSelected(product)}>
                <Typography variant="body1">{product.name}</Typography>

                <Typography className="mt-1 description" variant="caption" color="textSecondary">
                  {product.body.description}
                </Typography>
              </div>

              <Grid container alignItems="center">
                <Grid item xs={12}>
                  {product.body.sale && product.body.sale.percentage ? (
                    <div className="d-flex align-items-center">
                      <Typography variant="subtitle1" color="primary">
                        <Currency value={product.body.sale.value} />
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        className={classes.crossedPrice}
                      >
                        <Currency value={product.price} />
                      </Typography>
                    </div>
                  ) : (
                    <Typography variant="subtitle2" color="textSecondary">
                      <Currency value={product.price} />
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} className="mt-2">
                  {!product.body ||
                    ((!Boolean(product.body.features) ||
                      (product.body.features && product.body.features.length === 0)) &&
                      renderButton())}
                </Grid>
              </Grid>
            </CardContent>

            <CardActionArea className="media" onClick={() => onSelected(product)}>
              <CardMedia title={product.name}>
                {product.body.sale && product.body.sale.percentage && (
                  <div className={classes.sale}>{product.body.sale.percentage}%</div>
                )}
                {product.body.featured && <div className={classes.featured}></div>}
                <ImageLoader
                  imgKey={product.medias[0] && product.medias[0].key}
                  circularProgressSize={30}
                />
              </CardMedia>
            </CardActionArea>
          </div>
        )}
      </Card>
    </Badge>
  );
};

export default ProductListItem;
